<template>
  <div class="h3 mb-4">Варианты размещения</div>
  <div class="paragraph-m text-secondary mb-10">
    Отметьте варианты размещения, которые хотите использовать. Отсутствие выбора на Варианте размещение приведет к
    удалению Варианта размещения и утрате данных по ограничениям на Варианты размещения.
  </div>
  <div data-wide-content>
    <div v-if="props.placingVariants.placingVariantsTarification" class="placing-variants__filter">
      <e-select
        v-model="filterRoomCategory"
        :items="props.placingVariants.placingVariantsTarification"
        :error="filterRoomCategory && isEmptyPlacingVariants"
        item-title="roomCategory.name[0].value"
        item-value="roomCategory.cid"
        label="Категория номера"
        clearable
        class="placing-variants__filter-input"
      />
      <e-select
        v-model="filterTouristType"
        :items="touristsTypesFilterItems"
        :error="filterTouristType && isEmptyPlacingVariants"
        label="Тип туриста"
        clearable
        class="placing-variants__filter-input"
      />
      <e-checkbox v-model="filterCheckedOnly" label="Только выбранные" class="placing-variants__checkbox" />
      <div v-if="activeTariff?.baseSettings?.completed" class="placing-variants__checkbox d-flex">
        <e-checkbox v-model="filterConflictedOnly" label="Только конфликтные" class="with-badge" />
        <help-tooltip class="ml-1">
          Конфликтные варианты размещения - это варианты размещения связанные с удаленными сущностями тарифного плана,
          например относящиеся к удаленной из тарифного плана категории номеров или удаленному типу туриста.
        </help-tooltip>
      </div>
    </div>
  </div>
  <e-form v-model="isValid" ref="formRef" data-wide-content class="mt-6" @submit="submit">
    <placing-variants-tables
      v-model="items"
      :filter-checked-only="filterCheckedOnly"
      :filter-room-category="filterRoomCategory"
      :filter-tourist-type="filterTouristType"
      :filter-conflicted-only="filterConflictedOnly"
      :is-dirty="isDirty"
    />
  </e-form>

  <div class="d-flex justify-space-between mt-10">
    <e-btn prepend-icon-src="arrowLeft" variant="secondary" @click="back">Назад</e-btn>
    <e-btn :loading="loading" variant="primary" @click="save">Завершить</e-btn>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import type { EFormType } from '@/core/types/components/components';
import type {
  PVMPlacingVariant,
  PVMPlacingVariants,
  PVMPlacingVariantsItem,
  SuggestedPlacingVariant,
} from '@/parts/tariffs/types/tariffs';
import { EBtn, ECheckbox, EForm, ESelect, HelpTooltip } from '@/core/components/ui';
import PlacingVariantsTables from '@/parts/tariffs/components/PlacingVariantsTables.vue';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';
import { rangeTouristTypesString } from '@/parts/tariffs/utils/tariffs';

const props = defineProps<{
  placingVariants: PVMPlacingVariants;
  loading: boolean;
  touristTypes: {
    from: number | null;
    to: number | null;
  }[];
}>();

const emit = defineEmits<{
  (event: 'next', placingVariants: PVMPlacingVariantsItem[]): void;
  (event: 'back'): void;
}>();

const { activeTariff } = useTariffsStore();

const filterRoomCategory = ref(null);
const filterTouristType = ref(null);
const filterCheckedOnly = ref(false);
const filterConflictedOnly = ref(false);

const isValid = ref(false);

const items = ref<PVMPlacingVariantsItem[]>([]);

const setItems = () => {
  if (!props.placingVariants.placingVariantsTarification) {
    items.value = [];

    return;
  }

  const placingVariantsTarification = props.placingVariants.placingVariantsTarification;

  items.value =
    placingVariantsTarification.map((tarification) => {
      const suggestedPlacingVariants = tarification.conflictedPlacingVariants.map((x) => ({
        ...x,
        isConflicted: true,
      })) as SuggestedPlacingVariant & { isConflicted?: boolean };

      suggestedPlacingVariants.unshift(...tarification.suggestedPlacingVariants);

      const placingVariants = [];

      suggestedPlacingVariants.forEach((placingVariant) => {
        placingVariants.push({
          ...placingVariant,
          tarification: placingVariant.tarification
            ? placingVariant.tarification
            : {
                roomPlacingCid: '',
                pricePerRoom: false,
                isNeedConfirm: false,
                maxNoSeatQuantity: 0,
                defaultAvailable: true,
              },
        });
      });

      const item = {
        roomCid: tarification.roomCategory.cid,
        roomName: tarification.roomCategory.name[0].value,
        placingVariants: suggestedPlacingVariants.map((placingVariant) => ({
          ...placingVariant,
          isLastVariant:
            suggestedPlacingVariants.filter((x) => x.isChecked).length === 1 && placingVariant.isChecked ? true : false,
          tarification: placingVariant.tarification
            ? placingVariant.tarification
            : {
                roomPlacingCid: '',
                pricePerRoom: false,
                isNeedConfirm: false,
                maxNoSeatQuantity: 0,
                defaultAvailable: true,
              },
        })),
        selected: [] as PVMPlacingVariant[],
        massOperationMaxNoSeatQuantity: null,
        isMassOperationActive: false,
        isMassOperationPricePerRoom: false,
      };

      item.selected = item.placingVariants.filter((placingVariant) => placingVariant.isChecked);

      return item;
    }) || [];
};

setItems();

const touristsTypesFilterItems = toRef(() =>
  props.touristTypes.map((type) => ({
    title: rangeTouristTypesString(type),
  })),
);

const isDirty = ref(false);

const isEmptyPlacingVariants = computed(
  () => isDirty.value && items.value.some((roomCategory) => !roomCategory.selected.length),
);
const submit = () => {
  const hasEmptyPlacingVariants = items.value.find((variant) => !variant.selected.length);

  if (hasEmptyPlacingVariants || !isValid.value) {
    return;
  }

  emit('next', items.value);
};

const formRef = ref<EFormType>();

const save = () => {
  isDirty.value = true;

  formRef.value?.submit();
};

const back = () => {
  emit('back');
};
</script>

<style scoped lang="scss">
.placing-variants {
  &__filter {
    display: flex;
    align-items: center;
    gap: $gap-4;

    @include sm-and-down {
      flex-wrap: wrap;
    }

    &-input {
      width: 100%;
      @include sm-and-down {
        width: calc((100% - $gap-4) / 2);
      }
      @include xs {
        width: 100%;
      }
    }
  }

  &__checkbox {
    flex-grow: 1;
    flex-shrink: 0;
  }
}
</style>
